import { gql } from "@apollo/client";

export const LOGIN = gql`
  mutation Login($input: LoginFormInput!) {
    login(input: $input) {
      success
      message
      token
      user {
        userUuid
        name
        email
        active
      }
      company {
        companyUuid
        name
        active
        companyType
        type
      }
    }
  }
`;

export const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($input: RequestPasswordResetFormInput!) {
    requestPasswordReset(input: $input) {
      message
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($input: ResetPasswordFormInput!) {
    resetPassword(input: $input) {
      message
    }
  }
`;

export const GET_ME = gql`
  query {
    me {
      id
      userIsAdmin
      userUuid
      name
      email
      active
    }
  }
`;

export const GET_POLICY_LIST = gql`
  query GetPolicyList(
    $sortBy: PolicyListSortBy
    $sortDirection: SortDirection
    $page: Int
    $first: Int! = 10
    $filter: PolicyListFilterInput
  ) {
    getPolicyList(
      sortBy: $sortBy
      sortDirection: $sortDirection
      page: $page
      first: $first
      filter: $filter
    ) {
      data {
        policyUuid
        policyNumber
        status
        createdAt
        policyStatus
        policyMembersStatus
        user {
          userUuid
          name
          email
          active
        }
        integration {
          name
          integrationUuid
          integrationType
          active
          createdAt
        }
        fileImport {
          fileImportUuid
        }
        company {
          companyUuid
          name
          active
        }
        policiable {
          ... on SlashDataPolicy {
            id
            policyNumber
            planName
            policyStartDate
            policyExpiryDate
            icRequestRefNo
          }
          ... on ShafafiyaPolicy {
            id
            shafafiyaReferenceNo
            validityDate
            totalPenalty
            members {
              id
              eid
            }
          }
        }
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export const GET_POLICY_STATUS_COUNT = gql`
  query GetPolicyStatusCount($integrationUuid: String) {
    getPolicyStatusCount(integrationUuid: $integrationUuid) {
      status
      count
    }
  }
`;

export const GET_FILE_IMPORT_STATUS_COUNT = gql`
  query GetFileImportStatusCount($integrationUuid: String) {
    getFileImportStatusCount(integrationUuid: $integrationUuid) {
      status
      count
    }
  }
`;

export const IMPORT_POLICY_FILE = gql`
  mutation ImportPolicyFile($input: ImportPolicyFileInput!) {
    importPolicyFile(input: $input) {
      success
      message
      errorLines
      fileImportUuid
    }
  }
`;

export const IMPORT_COC_FILE = gql`
  mutation ImportCOCFile($input: ImportCOCFileInput!) {
    importCOCFile(input: $input) {
      success
      message
      errorLines
      fileImportUuid
    }
  }
`;

export const CREATE_POLICY = gql`
  mutation CreatePolicy($input: CreatePolicyInput!) {
    createPolicy(input: $input)
  }
`;

export const GET_INTEGRATION_LIST = gql`
  query GetIntegrationList {
    getIntegrationList {
      data {
        name
        integrationUuid
        integrationType
        active
        createdAt
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export const GET_POLICY_DETAILS = gql`
  query GetPolicyDetails($policyUuid: String!) {
    getPolicyDetails(policyUuid: $policyUuid) {
      policyUuid
      policyNumber
      user {
        userUuid
        name
      }
      integration {
        integrationUuid
        name
        integrationType
      }
      fileImport {
        fileImportUuid
      }
      company {
        companyUuid
        name
      }
      status
      fromApi
      createdAt
      policyStatus
      policyMembersStatus
      policiable {
        ... on SlashDataPolicy {
          id
          icRequestRefNo
          policyNumber
          policyIssueDate
          policyStartDate
          policyExpiryDate
          policyType
          policyOwnerType
          policyOwnerName
          policyOwnerId
          planName
          operationType
          policyCreationRefNo
          policyCancellationRefNo
          cancellationDate
          integrationStatus
        }
        ... on ShafafiyaPolicy {
          id
          shafafiyaReferenceNo
          totalPenalty
          validityDate
          shafafiyaIntegrationStatus: integrationStatus
          shafafiyaPolicyType: policyType
        }
      }
    }
  }
`;

export const GET_SHAFAFIYA_POLICY_MEMBERS = gql`
  query GetShafafiyaPolicyMembers(
    $policyUuid: String!
    $page: Int
    $first: Int! = 15
  ) {
    getShafafiyaPolicyMembers(policyUuid: $policyUuid, page: $page, first: $first) {
      data {
        id
        shafafiyaReferenceNo
        eid
        uid
        penalty
        nonInsuranceDays
        integrationStatus
        errorCode
        createdAt
        updatedAt
      }
      paginatorInfo {
        currentPage
        lastPage
        total
      }
    }
  }
`;

export const GET_SLASHDATA_POLICY_MEMBERS = gql`
  query GetSlashdataPolicyMembers(
    $policyUuid: String!
    $page: Int
    $first: Int! = 15
  ) {
    getSlashdataPolicyMembers(policyUuid: $policyUuid, page: $page, first: $first) {
      data {
        id
        memberRefNo
        enrolmentIssueDate
        enrolmentStartDate
        unifiedNo
        emiratesIdNo
        visaFileNo
        birthCertNo
        passportNo
        gender
        nationalityCode
        dateOfBirth
        fullNameEn
        firstNameEn
        middleNameEn
        lastNameEn
        fullNameAr
        firstNameAr
        middleNameAr
        lastNameAr
        maritalStatus
        relationWithSponsor
        memberIdType
        memberType
        sponsorIdNo
        sponsorIdType
        membershipCardNo
        className
        salaryBand
        occupationDesc
        emiratesOfVisaCode
        emiratesOfLivingCode
        memberCreationRefNo
        memberUpdateRefNo
        memberCancelRefNo
        cancellationDate
        integrationStatus
        status
        errors
        createdAt
        updatedAt
      }
      paginatorInfo {
        currentPage
        lastPage
        total
      }
    }
  }
`;

export const GET_FILE_IMPORT_LIST = gql`
  query GetFileImportList(
    $page: Int
    $first: Int! = 10
    $filter: FileImportListFilterInput
  ) {
    getFileImportList(page: $page, first: $first, filter: $filter) {
      data {
        fileImportUuid
        integration {
          name
          integrationUuid
          integrationType
          active
          createdAt
        }
        user {
          userUuid
          name
          email
          active
        }
        filename
        totalRecords
        importedRecords
        failedRecords
        errorFilepath
        errors
        status
        startedAt
        completedAt
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export const GENERATE_COC_CSV = gql`
  mutation GenerateCocCsv($fileImportUuid: String!, $type: String) {
    generateCocCsv(fileImportUuid: $fileImportUuid, type: $type) {
      success
      message
      csvFile
    }
  }
`;

export const REPROCESS_FILE_IMPORT = gql`
  mutation ReprocessFileImport($fileImportUuid: String!) {
    reprocessFileImport(fileImportUuid: $fileImportUuid)
  }
`;

export const GET_COC_IMPORT_LIST = gql`
  query getCOCImportList(
    $page: Int
    $first: Int! = 10
    $filter: COCImportListFilterInput
  ) {
    getCOCImportList(page: $page, first: $first, filter: $filter) {
      data {
        shafafiyaReferenceNo
        eid
        uid
        totalPenalty
        penalty
        nonInsuranceDays
        validityDate
        policyType
        integrationStatus
        errorCode
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export const GET_SHAFAFIYA_HISTORY = gql`
  query getShafafiyaHistory($eid: String!) {
    getShafafiyaHistory(eid: $eid) {
      firstName
      middleName
      lastName
      birthdate
      policyStartDate
      policyRenewalDate
      policyExpiryDate
      insuranceLicense
      status
      relation
      relationTo
      unifiedNumber
      emiratesId
      transactionDate
    }
  }
`;

export const CANCEL_POLICY = gql`
  mutation CancelPolicy($policyUuid: String!) {
    cancelPolicy(policyUuid: $policyUuid)
  }
`;

export const GET_MEMBER_LIST = gql`
  query GetMemberList(
    $sortBy: MemberListSortBy
    $sortDirection: SortDirection
    $page: Int
    $first: Int! = 10
    $filter: MemberListFilterInput
  ) {
    getMemberList(
      sortBy: $sortBy
      sortDirection: $sortDirection
      page: $page
      first: $first
      filter: $filter
    ) {
      data {
        id
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export const GET_MEMBER_STATUS_COUNT = gql`
  query GetMemberStatusCount($integrationUuid: String) {
    getMemberStatusCount(integrationUuid: $integrationUuid) {
      status
      count
    }
  }
`;

export const GET_SLASHDATA_MEMBER = gql`
  query GetSlashdataMember($memberUuid: String!) {
    getSlashdataMember(memberUuid: $memberUuid) {
      id
      slashDataPolicy {
        policy {
          policyUuid
        }
      }
      memberRefNo
      enrolmentIssueDate
      enrolmentStartDate
      unifiedNo
      emiratesIdNo
      visaFileNo
      birthCertNo
      passportNo
      gender
      nationalityCode
      dateOfBirth
      fullNameEn
      firstNameEn
      middleNameEn
      lastNameEn
      fullNameAr
      firstNameAr
      middleNameAr
      lastNameAr
      maritalStatus
      relationWithSponsor
      memberIdType
      memberType
      sponsorIdNo
      sponsorIdType
      membershipCardNo
      className
      salaryBand
      occupationDesc
      emiratesOfVisaCode
      emiratesOfLivingCode
      memberCreationRefNo
      memberUpdateRefNo
      memberCancelRefNo
      cancellationDate
      integrationStatus
      status
      errors
      createdAt
      updatedAt
    }
  }
`;

export const REPORT_SEARCH = gql`
  mutation ReportSearch(
    $page: Int
    $first: Int! = 10
    $input: ReportSearchInput!
  ) {
    reportSearch(page: $page, first: $first, input: $input) {
      data {
        slashDataPolicy {
          policyNumber
          policyIssueDate
          policyStartDate
          policyExpiryDate
          policyType
          policyOwnerType
          policyOwnerName
          policyOwnerId
          planName
        }
        memberAdditionDate: createdAt
        memberRefNo
        enrolmentIssueDate
        enrolmentStartDate
        unifiedNo
        emiratesIdNo
        visaFileNo
        birthCertNo
        passportNo
        gender
        nationalityCode
        dateOfBirth
        fullNameEn
        firstNameEn
        middleNameEn
        lastNameEn
        fullNameAr
        firstNameAr
        middleNameAr
        lastNameAr
        maritalStatus
        relationWithSponsor
        memberIdType
        memberType
        sponsorIdNo
        sponsorIdType
        membershipCardNo
        salaryBand
        occupationDesc
        emiratesOfVisaCode
        emiratesOfLivingCode
        integrationStatus
        status
        className
        memberCreationRefNo
        memberCancelRefNo
        cancellationDate
        createdAt
        updatedAt
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export const DOWNLOAD_REPORT = gql`
  mutation DownloadReport($input: ReportSearchInput!) {
    downloadReport(input: $input)
  }
`;

export const RECHECK_MEMBER_STATUS = gql`
  mutation ReCheckMemberStatus($policyUuid: String!) {
    reCheckMemberStatus(policyUuid: $policyUuid)
  }
`;
export const RECHECK_COC_STATUS = gql`
  mutation ReCheckCOCStatus($eid: String!) {
    reCheckCocStatus(eid: $eid) {
      success
      policy {
        policyUuid
      }
    }
  }
`;

export const RECHECK_COC_IMPORT_MEMBER = gql`
  mutation ReCheckCocImportMember($eid: String!) {
    reCheckCocImportMember(eid: $eid) {
      success
      message
      shafafiyaReferenceNo
      eid
      uid
      totalPenalty
      penalty
      nonInsuranceDays
      validityDate
      errorCode
    }
  }
`;

export const CANCEL_SLASHDATA_MEMBER = gql`
  mutation CancelSlashdataMember($slashdataMemberUuid: String!) {
    cancelSlashdataMember(slashdataMemberUuid: $slashdataMemberUuid)
  }
`;

export const UPDATE_SLASHDATA_MEMBER = gql`
  mutation UpdateSlashdataMember(
    $memberUuid: String!
    $input: UpdateSlashdataMemberInput!
  ) {
    updateSlashdataMember(memberUuid: $memberUuid, input: $input) {
      memberRefNo
      memberUpdateRefNo
      integrationStatus
      errors
      updatedAt
    }
  }
`;

export const CANCEL_SHAFAFIYA_MEMBER = gql`
  mutation CancelShafafiyaMember($referenceNo: String!) {
    cancelShafafiyaMember(referenceNo: $referenceNo)
  }
`;

export const RESYNC_ALL_FAILED_COC_MEMBERS = gql`
  mutation ReSyncAllFailedCocMembers($fileImportUuid: String!, $submitAtOnce: Boolean!) {
    reSyncAllFailedCocMembers(fileImportUuid: $fileImportUuid, submitAtOnce: $submitAtOnce)
  }
`;

export const CREATE_SHAFAFIYA_POLICY = gql`
  mutation CreateShafafiyaPolicy($input: CreateShafafiyaPolicyInput!) {
    createShafafiyaPolicy(input: $input)
  }
`;

export const VALIDATION_QUOTES = gql`
  query ValidationQuotes {
    validationQuotes {
      company
      quotes {
        uid
        product
        reference
        policy_number
        sponsor_name
        total_premium
        created_at
        paid_at
        hostname
        quote_link
        state
        status
        sponsor {
          fullName
          emiratesIDNo
          tradeLicenseNumber
          attachments
        }
        members {
          emiratesIDNo
          unifiedNo
          visaFileNo
          birthCertNo
          fullNameEn
          firstNameEn
          middleName
          lastNameEn
          dateOfBirth
          gender
          residenceVisaCity
          nationality
          attachments
        }
      }
    }
  }
`;

export const UPDATE_VALIDATION_QUOTE_STATUS = gql`
  mutation UpdateQuote($input: UpdateValidationQuoteStatusInput!) {
    updateValidationQuoteStatus(input: $input) {
      success
      message
      state
      quote_uid
      hostname
    }
  }
`;

export const GET_ACTIVITY_LOGS = gql`
  query GetActivityLogs($policyUuid: String, $memberId: ID, $first: Int!, $page: Int!, $filter: ActivityLogFilterInput) {
    getActivityLogs(policyUuid: $policyUuid, memberId: $memberId, first: $first, page: $page, filter: $filter) {
      data {
        id entryType event integration subjectType subjectId policyUuid memberId
        actorId actorName metadata createdAt integrationAttemptId outcome operationId
        durationMs httpStatus errorCode errorMessage
      }
      paginatorInfo { currentPage lastPage total }
    }
  }
`;

export const GET_INTEGRATION_ATTEMPT = gql`
  query GetIntegrationAttempt($id: ID!) {
    getIntegrationAttempt(id: $id) {
      id operationId outcome endpoint request response httpStatus durationMs
      errorCode errorMessage createdAt finishedAt
    }
  }
`;
